// Frontend permission map — Build Prompt #14: 14-role hierarchy.
// Permission catalog mirrors backend PERMISSIONS in /app/backend/server.py.
// Backend remains the source of truth; UI uses these to hard-mask buttons/tabs from the DOM.

export const ROLE_LABELS = {
    super_admin: "Super Admin",
    chief_of_operations: "Chief of Operations",
    strategic_oversight: "Strategic Oversight",
    systems_lead: "Systems Lead",
    systems_operator: "Systems Operator",
    field_lead: "Field Lead",
    field_operator: "Field Operator",
    supply_lead: "Supply Lead",
    supply_operator: "Supply Operator",
    demand_lead: "Demand Lead",
    demand_operator: "Demand Operator",
    logistics_command: "Logistics Command",
    logistics_operator: "Logistics Operator",
    mission_partner: "Mission Partner",
};

// Visual identity — Night Signal Orange (#FF6A00) badges for Oversight + Super Admin per spec.
export const ROLE_BADGE_COLOR = {
    super_admin: "border-signal bg-signal text-jet font-bold",
    chief_of_operations: "border-signal text-signal",
    strategic_oversight: "border-signal bg-signal text-jet font-bold",
    systems_lead: "border-snow text-snow",
    systems_operator: "border-snow-dim text-snow-dim",
    field_lead: "border-snow text-snow",
    field_operator: "border-snow-dim text-snow-dim",
    supply_lead: "border-snow text-snow",
    supply_operator: "border-snow-dim text-snow-dim",
    demand_lead: "border-snow text-snow",
    demand_operator: "border-snow-dim text-snow-dim",
    logistics_command: "border-snow text-snow",
    logistics_operator: "border-snow-dim text-snow-dim",
    mission_partner: "border-[#2A2A2A] text-snow-muted",
};

// Canonical permission catalog — keep keys in lockstep with backend PERMISSIONS dict.
export const ALL_PERMISSIONS = [
    "manage_users", "manage_invitations", "manage_roles_engine", "view_system_health",
    "manage_templates", "manage_control_modes",
    "create_product", "edit_product", "delete_product", "view_products", "view_products_limited",
    "approve_gate", "submit_gate_for_review", "upload_deliverable", "request_approval", "kill_switch",
    "manage_inventory", "view_inventory", "delete_inventory",
    "manage_orders", "view_orders", "transition_order", "approve_order_override",
    "manage_vendors", "view_vendors", "view_logistics",
    "file_field_report", "view_field_intel", "resolve_field_report", "delete_field_report",
    "initiate_recert",
    "view_all_ideas", "submit_idea", "edit_own_idea", "review_ideas", "delete_idea",
    "append_concept_assets",
    "view_concept_intake", "view_dashboard_oversight",
];

// Role → permission list. `super_admin` is special — it bypasses all checks.
export const ROLE_PERMISSIONS = {
    super_admin: ["bypass_all"],

    chief_of_operations: [
        "manage_users", "manage_invitations", "manage_roles_engine", "view_system_health",
        "create_product", "edit_product", "delete_product", "view_products",
        "approve_gate", "submit_gate_for_review", "upload_deliverable", "request_approval", "kill_switch",
        "manage_inventory", "view_inventory", "delete_inventory",
        "manage_orders", "view_orders", "transition_order", "approve_order_override",
        "manage_vendors", "view_vendors", "view_logistics",
        "file_field_report", "view_field_intel", "resolve_field_report", "delete_field_report",
        "initiate_recert",
        "view_all_ideas", "submit_idea", "edit_own_idea", "review_ideas", "delete_idea",
        "view_concept_intake", "view_dashboard_oversight",
        "append_concept_assets",
    ],

    strategic_oversight: [
        "view_products", "view_logistics", "view_inventory", "view_orders", "view_vendors",
        "view_field_intel", "view_concept_intake", "view_dashboard_oversight",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    systems_lead: [
        "approve_gate", "submit_gate_for_review", "upload_deliverable", "request_approval",
        "view_products",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    systems_operator: [
        "upload_deliverable", "view_products",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    field_lead: [
        "file_field_report", "view_field_intel", "initiate_recert",
        "view_products",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    field_operator: [
        "file_field_report", "view_field_intel",
        "view_products",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    supply_lead: [
        "manage_vendors", "view_vendors",
        "view_logistics", "view_inventory", "view_orders",
        "view_concept_intake",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    supply_operator: [
        "view_vendors", "view_logistics", "view_inventory", "view_orders",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    demand_lead: [
        "manage_orders", "approve_order_override", "transition_order",
        "view_orders", "view_inventory", "view_logistics",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    demand_operator: [
        "manage_orders", "view_orders", "view_logistics",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    logistics_command: [
        "manage_inventory", "manage_orders", "transition_order",
        "view_inventory", "view_orders", "view_logistics",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    logistics_operator: [
        "manage_inventory", "view_inventory", "view_logistics",
        "view_all_ideas", "submit_idea", "edit_own_idea",
        "append_concept_assets",
    ],

    mission_partner: [
        "view_products_limited",
        "view_all_ideas", "submit_idea", "edit_own_idea",
    ],
};

// Camel-case → snake-case compat for legacy call sites still using `can(user, "killSwitch")`.
// New code should pass the canonical snake_case permission name directly.
const LEGACY_PERM_ALIAS = {
    manageTemplates: "manage_templates",
    manageControlModes: "manage_control_modes",
    manageProducts: "edit_product",
    createProduct: "create_product",
    deleteProduct: "delete_product",
    approveGate: "approve_gate",
    submitGateForReview: "submit_gate_for_review",
    uploadDeliverable: "upload_deliverable",
    requestApproval: "request_approval",
    killSwitch: "kill_switch",
    manageInventory: "manage_inventory",
    deleteInventory: "delete_inventory",
    manageOrders: "manage_orders",
    transitionOrder: "transition_order",
    approveOrderOverride: "approve_order_override",
    fileFieldReport: "file_field_report",
    resolveFieldReport: "resolve_field_report",
    deleteFieldReport: "delete_field_report",
    initiateRecert: "initiate_recert",
    manageVendors: "manage_vendors",
    submitIdea: "submit_idea",
    reviewIdeas: "review_ideas",
};

/**
 * Core permission gate. Returns true if user has ANY of the listed permissions.
 * `super_admin` always returns true.
 */
export function hasPermission(user, ...perms) {
    if (!user || !user.role) return false;
    if (user.role === "super_admin") return true;
    const granted = new Set(ROLE_PERMISSIONS[user.role] || []);
    return perms.some((p) => granted.has(LEGACY_PERM_ALIAS[p] || p));
}

/** Convenience for templates that pass a single permission. */
export function can(user, permission) {
    return hasPermission(user, permission);
}

/** Idea ownership rule from spec: edit only if (author && status === 'Submitted'). */
export function canEditOwnIdea(user, idea) {
    if (!user || !idea) return false;
    if (!hasPermission(user, "edit_own_idea")) return false;
    return idea.submitter_id === user.id && idea.idea_status === "Submitted";
}

/** Hard-mask helper: returns null when no permission, otherwise the children. */
export function maskUnless(user, permission, children) {
    return hasPermission(user, permission) ? children : null;
}

// Tailwind class for a button that is rendered but explicitly disabled (rarely used now —
// the spec mandates DOM removal for hard-masking).
export const DISABLED_BTN_CLS =
    "!bg-[#2A2A2A] !text-snow-muted !border-[#2A2A2A] cursor-not-allowed opacity-70 hover:!bg-[#2A2A2A] hover:!text-snow-muted hover:!border-[#2A2A2A]";

export function permissionTitle(user, permission) {
    if (hasPermission(user, permission)) return undefined;
    const role = ROLE_LABELS[user?.role] || user?.role || "anonymous";
    return `Restricted — requires "${permission}". Current role: ${role}.`;
}

/**
 * Landing page per role (Build Prompt #14):
 *   Logistics / Supply roles → /logistics
 *   Demand roles → /orders
 *   Systems / Field roles → /products
 *   Oversight + Super Admin + Chief of Ops → /dashboard
 *   Mission Partner → /products
 */
export function landingPathForRole(role) {
    if (!role) return "/dashboard";
    if (role === "super_admin" || role === "chief_of_operations" || role === "strategic_oversight") return "/dashboard";
    if (role.startsWith("logistics_") || role.startsWith("supply_")) return "/logistics";
    if (role.startsWith("demand_")) return "/orders";
    if (role.startsWith("systems_") || role.startsWith("field_")) return "/products";
    if (role === "mission_partner") return "/products";
    return "/dashboard";
}
